import { GrayCardWrapper, Wrapper } from "./reviewAndCheckout.style";
import {
  Button,
  CardText,
  Col,
  Container,
  FormFeedback,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import deliveryIcon from "../../../../../../assets/icons/delivery.png";
import { IoLocationOutline } from "react-icons/io5";
import { CiCircleInfo } from "react-icons/ci";
import { RiArrowDropRightLine } from "react-icons/ri";
import { OrderSummaryStyle } from "../../../shoppingCart/section/cart/cart.style";
import { AuthCustomModalDescriptionStyleB } from "../../../../../../layouts/auth/auth.layout.style";
import { CartItemCardComponent } from "../../../../../../components/cartItemCard/cartItemCard.component";
import { memo } from "react";
import { AddressManagementModalComponent } from "../../../../../../components/addressManagement";
import { PageLoader } from "../../../../../../components/loaders";
import { NumericFormat } from "react-number-format";
import { AddDiscountCodeModalSection } from "../addDiscountCodeModal/addDiscountCodeModal.section";
import { CustomHelper } from "../../../../../../helpers/custom-helper";
import { IoIosArrowBack } from "react-icons/io";
import { CustomInput } from "../../../../../../theme/styles";
import { useReviewAndCheckout } from "../../../../../../hooks/reviewAndCheckout.hook";

const LoadingOverlay = () => (
  <div
    role="alert"
    aria-busy="true"
    className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
    style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 10500000 }}
  >
    <PageLoader />
  </div>
);

const OrderNotesSection = memo(({ orderNotes, onChange }) => (
  <GrayCardWrapper className="border-0 cart-delivery-info-pad checkout-order-notes tw-mb-2">
    <FormGroup className="mb-0">
      <Label htmlFor="checkout-order-notes">Order notes <span className="text-muted">(optional)</span></Label>
      <CustomInput id="checkout-order-notes" type="textarea" rows="3" value={orderNotes} onChange={(event) => onChange(event.target.value)} placeholder="Add any delivery instruction for your order." maxLength="500" />
      <small>Your note will be included with this order.</small>
    </FormGroup>
  </GrayCardWrapper>
));

const StaffEmailSection = memo(({ staffEmail, onChange }) => (
  <GrayCardWrapper className="border-0 cart-delivery-info-pad checkout-staff-email tw-mb-2">
    <FormGroup className="mb-0">
      <Label htmlFor="checkout-staff-email">Staff email</Label>
      <CustomInput id="checkout-staff-email" type="email" value={staffEmail} onChange={(event) => onChange(event.target.value)} placeholder="staff@agrogrid.com" />
      <small>This email will be notified when user has made payment.</small>
    </FormGroup>
  </GrayCardWrapper>
));

export const ReviewAndCheckoutSection = memo(() => {
  const { checkoutData,
    displayName,
    user,
    isAuthenticated,
    isGuestUser,
    navigate,

    // address / delivery
    selectedAddress,
    split_buyer_address,
    isAddressModalOpen,
    setIsAddressModalOpen,
    toggleAddressModal,
    handleAddressSelect,

    // pickup
    isPickup,
    pickupState,
    setPickupState,
    pickupLocations,
    pickupLoading,
    pickupError,
    setPickupError,
    selectedPickupLocationId,
    setSelectedPickupLocationId,
    availablePickupStates,
    handlePickupToggle,
    handlePickupSelectionChange,
    formatPickupLocationLabel,

    // checkout data / order summary
    combined_split_buys,
    cost_breakdown,
    payment_url,
    discount_applied,
    orderNotes,
    setOrderNotes,
    isStaffCheckout,
    staffEmail,
    setStaffEmail,

    // referral
    appliedReferralCode,
    isReferralModalOpen,
    toggleReferralModal,
    handleApplyReferralCode,

    // payment
    handleMakePayment,

    // loading state
    loading
  } = useReviewAndCheckout();
  if (!checkoutData) return <PageLoader />;

  // Helper components
  const HeaderSection = ({ title, onBack }) => (
    <div className="first_content align-items-center my-3 justify-content-between my-3 header">
      <div className="close-btn tw-cursor-pointer" onClick={onBack}>
        <IoIosArrowBack size={25} />
      </div>
      <h4 className="inherit-all !tw-flex-1 d-flex justify-content-center m-0">
        {title}
      </h4>
    </div>
  );

  const SectionHeader = ({ title }) => (
    <>
      <div className="first_content title my-md-3 header">{title}</div>
      <div className="cart-icons-line md-hidden" />
    </>
  );

  const DeliveryInfoSection = () => (
    <GrayCardWrapper className="border-1 tw-space-y-2 cart-delivery-info-pad cart-delivery-information">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div className="tw-space-y-2">
          <h5>
            Name: <span>{displayName}</span>
          </h5>
          <h5>
            Email:{" "}
            <span className="text-lowercase">
              {user?.email || "Not specified"}
            </span>
          </h5>
          <h5>
            Phone:{" "}
            <span>{user?.phone || user?.phonenumber || "Not specified"}</span>
          </h5>
          <h5>
            {isPickup ? "Pickup Location" : "Address"}:{" "}
            <span>
              {isPickup
                ? user?.pick_up_address ||
                formatPickupLocationLabel(
                  pickupLocations.find(
                    (location) => location?._id === selectedPickupLocationId
                  )
                ) ||
                "Not specified"
                : split_buyer_address?.street ||
                selectedAddress?.street ||
                selectedAddress?.address ||
                "Not specified"}
            </span>
          </h5>
        </div>
        {!isPickup && (
          <ChangeAddressButton
            onClick={() =>
              isGuestUser
                ? navigate("/guest-sign-in")
                : toggleAddressModal()
            }
          />
        )}
      </div>

      {isAuthenticated && (
        <div className="d-flex align-items-center justify-content-start mb-3">
          <label className="d-flex align-items-center tw-text-sm gap-2 mb-0">
            <input
              type="checkbox"
              checked={isPickup}
              onChange={(e) => handlePickupToggle(e.target.checked)}
            />
            I prefer pickup
          </label>
        </div>
      )}

      {isAuthenticated && isPickup ? (
        <>
          <FormGroup className="mb-3">
            <Label>
              Pickup State <span className={"required"}>*</span>
            </Label>
            <CustomInput
              type="select"
              value={pickupState}
              onChange={(e) => {
                setPickupState(e.target.value);
                setSelectedPickupLocationId("");
                setPickupError(null);
              }}
              disabled={!availablePickupStates.length}
            >
              <option value="">
                {availablePickupStates.length
                  ? "Select state"
                  : "No states currently available"}
              </option>
              {availablePickupStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </CustomInput>
          </FormGroup>

          <FormGroup className="mb-0">
            <Label>
              Pickup Location <span className={"required"}>*</span>
            </Label>
            <CustomInput
              type="select"
              value={selectedPickupLocationId}
              onChange={(e) => handlePickupSelectionChange(e.target.value)}
              disabled={!pickupState || pickupLoading || !!pickupError}
            >
              <option value="">
                {pickupLoading
                  ? "Loading pickup locations..."
                  : pickupError
                    ? "Choose another state"
                    : "Select pickup location"}
              </option>
              {pickupLocations.map((location) => (
                <option key={location?._id} value={location?._id}>
                  {formatPickupLocationLabel(location)}
                </option>
              ))}
            </CustomInput>
            {pickupError && (
              <FormFeedback className="d-block">{pickupError}</FormFeedback>
            )}
          </FormGroup>
          <p className="text-muted tw-text-sm m-0">
            You're to pick up at the above address. Use the change button to
            update delivery instruction.
          </p>
        </>
      ) : null}
    </GrayCardWrapper>
  );

  const ChangeAddressButton = ({ onClick }) => (
    <Button
      color="link"
      size="sm"
      onClick={onClick}
      className="p-0 tw-font-bold text-decoration-none"
    >
      Change
    </Button>
  );

  const DeliveryGroup = ({ group, groupIndex, totalGroups }) => {
    const firstItem = group[0];
    const deliveryDate = firstItem.delivery_date;
    const state = firstItem.state;

    return (
      <div key={groupIndex} className="delivery-group">
        <div className="delivery-group-header mb-3 p-3 bg-light rounded">
          <h5 className="mb-1">
            {isAuthenticated && isPickup ? "Pickup in" : "Delivery to"} {state}{" "}
            on{" "}
            {deliveryDate
              ? CustomHelper.formatDateToReadable(new Date(deliveryDate))
              : "Not specified"}
          </h5>
          <p className="text-muted mb-0">
            {group.length} item{group.length > 1 ? "s" : ""} in this delivery
          </p>
        </div>
        <DeliveryStepper
          group={group}
          isLastStep={groupIndex === totalGroups - 1}
        />
      </div>
    );
  };

  const DeliveryStepper = ({ group, isLastStep }) => {
    const names = group?.map((item) => item.split_buy_name) || [];
    const displayName =
      names.length === 1
        ? names[0]
        : names.length === 2
          ? `${names[0]} & ${names[1]}`
          : `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;

    return (
      <ol className="c-stepper mb-3">
        <li className={`c-stepper__item ${isLastStep ? "!tw-pb-0" : ""}`}>
          <div className="c-stepper__dot">
            <div />
          </div>
          <div className="c-stepper__content">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="title text-capitalize">{displayName}</h4>
              <h4 className="title">
                <NumericFormat
                  className="money"
                  value={group?.[0]?.delivery_fee ?? 0}
                  displayType="text"
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  prefix="₦"
                />
              </h4>
            </div>
            <CardText className="card-location flex-nowrap d-flex align-items-center">
              <b>
                {isAuthenticated && isPickup ? "Pickup" : "Delivery"}:{" "}
                {group?.[0]?.delivery_date
                  ? CustomHelper.formatDateToReadable(
                    new Date(group[0].delivery_date)
                  )
                  : "Not specified"}
              </b>
            </CardText>
          </div>
        </li>
      </ol>
    );
  };

  const CostBreakdownItem = ({ item }) => (
    <div
      className={`d-flex mb-2 align-items-center justify-content-between ${item.label.toLowerCase().includes("total") ? "total" : ""
        }`}
    >
      <h4 className="title simmer_down">{item.label}:</h4>
      <h5 className={`subtitle ${item.value < 0 ? "custom_app_discount" : ""}`}>
        <NumericFormat
          className="money"
          value={item.value ?? 0}
          displayType="text"
          thousandSeparator=","
          decimalScale={2}
          fixedDecimalScale
          prefix="₦"
        />
      </h5>
    </div>
  );

  const OrderSummaryItem = ({ item }) => (
    <>
      {item.label.toLowerCase().includes("total payment") && (
        <div className="cart-icons-line" />
      )}
      <div
        className={`first_content d-flex justify-content-between mb-md-3 mb-2 ${item.label.toLowerCase().includes("total payment")
          ? "header total"
          : "sub-total"
          }`}
      >
        <h4 className="title">{item.label}:</h4>
        <h6>
          <NumericFormat
            className="money"
            value={item.value ?? 0}
            displayType="text"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
            prefix="₦"
          />
        </h6>
      </div>
    </>
  );

  const DiscountCodeSection = () => (
    <GrayCardWrapper className="border-1 tw-space-y-2 cart-delivery-info-pad cart-delivery-information">
      <div
        onClick={toggleReferralModal}
        className="tw-cursor-pointer d-flex align-items-center justify-content-between flex-nowrap"
      >
        <CardText className="card-location flex-nowrap d-flex align-items-center">
          <h5 className="tw-whitespace-break-spaces">Discount Code</h5>
          <CiCircleInfo
            style={{ transform: "rotate(180deg)" }}
            color="#F0B149"
            strokeWidth={1}
          />
        </CardText>
        <CardText className="card-location flex-nowrap d-flex align-items-center">
          <span className="simmer_down tw-text-xs tw-whitespace-break-spaces">
            {appliedReferralCode ? "Change Code" : "Use Code"}
          </span>
          <RiArrowDropRightLine color="#CCCCCC" />
        </CardText>
      </div>
    </GrayCardWrapper>
  );

  return (
    <Wrapper>
      <Container>
        <HeaderSection
          title="Review And Checkout"
          onBack={() => navigate(-1)}
        />

        <Row xl={12}>
          <Col className="d-flex flex-column row-gap-md-0 row-gap-2" md={7}>
            <div>
              <SectionHeader
                title={isPickup ? "Pickup Information" : "Delivery Information"}
              />
              <DeliveryInfoSection />
            </div>

            <div>
              <SectionHeader
                title={
                  isPickup ? "Pickup Summary" : "Combined Delivery Summary"
                }
              />
              <div className="cart-delivery-info-pad border-1 cart-deliver-summary">
                <div className="d-flex delivery-address gap-2 mb-3 align-items-start">
                  <img
                    src={deliveryIcon}
                    width={31}
                    height={31}
                    alt="delivery icon"
                  />
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center mb-md-2 mb-0">
                      <h4 className="title mb-0">
                        {isPickup ? "Pickup Location" : "Delivery Address"}
                      </h4>
                      <ChangeAddressButton
                        onClick={() =>
                          isGuestUser
                            ? navigate("/guest-sign-in")
                            : toggleAddressModal()
                        }
                      />
                    </div>
                    <CardText className="card-location flex-nowrap d-flex align-items-center">
                      <IoLocationOutline className="me-1 me-md-2" />
                      {isPickup
                        ? user?.pick_up_address ||
                        formatPickupLocationLabel(
                          pickupLocations.find(
                            (location) =>
                              location?._id === selectedPickupLocationId
                          )
                        ) ||
                        "Not specified"
                        : split_buyer_address?.street ||
                        selectedAddress?.street ||
                        selectedAddress?.address ||
                        "Not specified"}
                    </CardText>
                  </div>
                </div>

                {combined_split_buys?.map((group, groupIndex) => (
                  <DeliveryGroup
                    key={groupIndex}
                    group={group}
                    totalGroups={combined_split_buys.length}
                    groupIndex={groupIndex}
                  />
                ))}

                <div className="amt-summary">
                  {cost_breakdown
                    ?.filter((item) => item?.value !== 0)
                    ?.map((cb) => (
                      <CostBreakdownItem key={cb.label} item={cb} />
                    ))}
                </div>
              </div>
            </div>
          </Col>

          <Col className="d-flex flex-column row-gap-md-0 row-gap-2" md={5}>
            <div>
              <SectionHeader title="My Order" />
              <div>
                {combined_split_buys?.flat()?.map((item, idx) => (
                  <CartItemCardComponent
                    key={idx}
                    title={item.split_buy_name}
                    location={item.state}
                    amount={(item.buy_in_amount ?? 0).toLocaleString()}
                    splits={item.fractional_number_of_slots_purchased}
                    image={item.split_buy_image_url}
                    deliveryDate={item.delivery_date}
                    join_period_end_date={item.split_buy_delivery_date}
                    left={false}
                  />
                ))}
              </div>
            </div>

            <OrderNotesSection orderNotes={orderNotes} onChange={setOrderNotes} />
            {isStaffCheckout && <StaffEmailSection staffEmail={staffEmail} onChange={setStaffEmail} />}

            <>
              <DiscountCodeSection />
              <div className="cart-icons-line" />
            </>
            <OrderSummaryStyle>
              {cost_breakdown
                ?.filter((item) => item?.value !== 0)
                ?.map((item) => (
                  <OrderSummaryItem key={item.label} item={item} />
                ))}

              <div className="cart-icons-line" />
              <div className="mobile-sticky-checkout">
                <AuthCustomModalDescriptionStyleB className="my-md-4 my-2">
                  By proceeding, you agree to our{" "}
                  <a href="/terms-and-conditions">Terms of Use</a> and{" "}
                  <a href="/privacy-policy">Privacy Policy</a>
                </AuthCustomModalDescriptionStyleB>

                <Button
                  className="mb-md-3 mb-0 w-100"
                  onClick={handleMakePayment}
                  disabled={
                    !payment_url || (isPickup && !selectedPickupLocationId)
                  }
                >
                  {payment_url
                    ? isStaffCheckout
                      ? "Generate Payment Link"
                      : "Make Payment"
                    : "Payment Unavailable"}
                </Button>
              </div>
            </OrderSummaryStyle>
          </Col>
        </Row>
      </Container>

      <AddressManagementModalComponent
        onSelect={handleAddressSelect}
        view="list"
        isOpen={isAddressModalOpen}
        toggle={toggleAddressModal}
      />

      {loading && <LoadingOverlay />}

      <AddDiscountCodeModalSection
        isOpen={isReferralModalOpen}
        toggle={toggleReferralModal}
        onApplyCode={handleApplyReferralCode}
      />
    </Wrapper>
  );
});
